import { marked, DOMPurify } from '../markdown';
import { getAvatarColor, getAvatarColorByName } from './avatar';
import { withFileAuth, isServerFilePath } from './secureFiles';

// 채팅 메시지 마크다운 → 안전한 HTML
// 1) marked 로 변환 → 2) DOMPurify 로 정화 → 3) 정화된 DOM 의 "텍스트 노드"에만 멘션/채널 칩을 DOM API로 삽입
// 정규식으로 HTML 문자열을 다시 조작하던 기존 방식은 속성값 안의 따옴표를 깨뜨려 XSS 로 이어질 수 있었다.

const SANITIZE_OPTIONS = {
    FORBID_TAGS: ['style', 'form', 'input', 'textarea', 'select', 'option', 'iframe', 'object', 'embed', 'link', 'meta'],
    FORBID_ATTR: ['style', 'formaction'],
    RETURN_DOM_FRAGMENT: true,
};

const WORD_CHAR = /[a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ_]/;
const SKIP_PARENTS = new Set(['CODE', 'PRE', 'A', 'BUTTON']);

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
};

const mentionChip = (username, userId) => {
    const chip = el('span', 'mention-chip', `@${username}`);
    const popup = el('span', 'mention-popup');
    const avatar = el('span', 'mention-popup-avatar', username.charAt(0).toUpperCase());
    avatar.style.backgroundColor = userId ? getAvatarColor(userId) : getAvatarColorByName(username);
    popup.append(avatar, el('span', 'mention-popup-name', username));
    chip.appendChild(popup);
    return chip;
};

// 공백이 포함된 닉네임(@홍 길동)도 인식하도록 접속자 목록 기반 패턴을 만든다
const buildTokenPattern = (onlineUsers) => {
    const spaced = (onlineUsers || [])
        .map(u => u.username)
        .filter(name => name && name.includes(' '))
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp);
    const parts = [
        '@\\[([^\\]|]+)\\|([^\\]]+)\\]',                          // 1,2: @[userId|닉네임]
        spaced.length ? `@(${spaced.join('|')})(?![a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ_])` : null, // 3: 공백 닉네임
        '@([a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ_]+)',                          // 4: @닉네임
        '#([a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ_]+)',                          // 5: #채널
    ].filter(Boolean);
    return { re: new RegExp(parts.join('|'), 'g'), hasSpaced: spaced.length > 0 };
};

const decorateText = (textNode, ctx) => {
    const text = textNode.nodeValue;
    if (!text || (!text.includes('@') && !text.includes('#'))) return;
    const { re, hasSpaced } = ctx.pattern;
    re.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let last = 0, changed = false, m;
    while ((m = re.exec(text)) !== null) {
        const [whole] = m;
        const tagged = m[1] !== undefined;
        const spacedName = hasSpaced ? m[3] : undefined;
        const word = hasSpaced ? m[4] : m[3];
        const channelName = hasSpaced ? m[5] : m[4];
        // 태그 멘션 외에는 앞 글자가 단어 문자면 무시 (이메일 주소 등)
        if (!tagged && m.index > 0 && WORD_CHAR.test(text[m.index - 1])) continue;

        let node = null;
        if (tagged) node = mentionChip(m[2], m[1]);
        else if (spacedName) node = mentionChip(spacedName, ctx.onlineUsers.find(u => u.username === spacedName)?.userId);
        else if (word === 'everyone' || word === 'here') node = el('span', 'mention-chip global', `@${word}`);
        else if (word) node = mentionChip(word);
        else if (channelName) {
            const channel = ctx.channels.find(c => c.name === channelName);
            if (channel) {
                node = el('span', 'channel-mention-chip', whole);
                node.dataset.channelId = String(channel.id);
            }
        }
        if (!node) continue;
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        frag.appendChild(node);
        last = m.index + whole.length;
        changed = true;
    }
    if (!changed) return;
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    textNode.replaceWith(frag);
};

const decorateLinks = (root) => {
    root.querySelectorAll('a').forEach((a) => {
        // 외부 코드가 넣은 "돌아가기" 버튼 링크는 비활성화
        if (a.classList.contains('back-btn')) {
            a.removeAttribute('href');
            a.removeAttribute('target');
            a.setAttribute('role', 'button');
            a.setAttribute('aria-disabled', 'true');
            a.classList.add('disabled-chat-link');
            return;
        }
        const href = a.getAttribute('href');
        if (href && isServerFilePath(href)) a.setAttribute('href', withFileAuth(href));
    });
    root.querySelectorAll('img').forEach((img) => {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
        img.setAttribute('referrerpolicy', 'no-referrer');
        const src = img.getAttribute('src');
        if (src && isServerFilePath(src)) img.setAttribute('src', withFileAuth(src));
    });
};

export const renderMessageHtml = (markdown, { channels = [], onlineUsers = [] } = {}) => {
    const source = String(markdown ?? '').replace(/^[ \t]*$/gm, '\u00a0'); // 빈 줄 유지 (NBSP)
    const fragment = DOMPurify.sanitize(marked.parse(source), SANITIZE_OPTIONS);
    const container = document.createElement('div');
    container.appendChild(fragment);
    decorateLinks(container);

    const ctx = { channels, onlineUsers, pattern: buildTokenPattern(onlineUsers) };
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            for (let p = node.parentNode; p && p !== container; p = p.parentNode) {
                if (SKIP_PARENTS.has(p.nodeName)) return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
        },
    });
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach(node => decorateText(node, ctx));
    return container.innerHTML;
};

// 공지 등 멘션 처리가 필요 없는 마크다운
export const renderPlainMarkdown = (markdown) => {
    const container = document.createElement('div');
    container.appendChild(DOMPurify.sanitize(marked.parse(String(markdown ?? '')), SANITIZE_OPTIONS));
    decorateLinks(container);
    return container.innerHTML;
};
